/**
 * Staff Guide: Getting Started - Device Registration
 * Simple one-page printable PDF guide
 */
import { Printer, Smartphone, Wifi, CheckCircle } from 'lucide-react';

export default function StaffGettingStartedGuide() {
  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>

      <div className="min-h-screen bg-white">
        {/* Header with Print Button */}
        <div className="no-print bg-green-700 text-white py-4 px-6 flex justify-between items-center sticky top-0 z-50">
          <div className="flex items-center gap-3">
            <Smartphone className="w-6 h-6" />
            <div>
              <h1 className="font-bold text-lg">Getting Started Guide</h1>
              <p className="text-green-100 text-sm">Device Registration</p>
            </div>
          </div>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 bg-white text-green-700 px-4 py-2 rounded-lg font-semibold hover:bg-green-50 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print / Download PDF
          </button>
        </div>

        {/* Content */}
        <div className="max-w-2xl mx-auto p-8">
          {/* Title */}
          <div className="text-center mb-8 pb-6 border-b-2 border-green-600">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Smartphone className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Getting Started</h1>
            <p className="text-xl text-green-700">Device Registration</p>
            <p className="text-gray-500 mt-2">Bulaccino Cafe - Nadi, Fiji</p>
          </div>

          {/* Why Register */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <h2 className="font-bold text-amber-800 text-lg mb-2">Why Do I Need to Register My Device?</h2>
            <p className="text-amber-700">
              To prevent buddy punching, each staff member must register their personal phone. 
              You can only clock in/out from your registered device while connected to cafe Wi-Fi.
            </p>
          </div>

          {/* Step 1 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg">1</div>
              <h2 className="text-xl font-bold text-gray-900">Connect to Cafe Wi-Fi</h2>
            </div>
            <div className="ml-13 pl-6 border-l-2 border-green-200">
              <div className="flex items-center gap-2 mb-3">
                <Wifi className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-gray-700">Connect to one of these networks:</span>
              </div>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <code className="bg-gray-100 px-2 py-1 rounded">BC-Namaka-S</code>
                </li>
              </ul>
              <p className="text-gray-500 text-sm mt-3 italic">This is the secure staff network. Do not use customer Wi-Fi networks.</p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg">2</div>
              <h2 className="text-xl font-bold text-gray-900">Go to the Staff Portal</h2>
            </div>
            <div className="ml-13 pl-6 border-l-2 border-green-200">
              <p className="text-gray-600 mb-3">Open your phone's web browser and go to:</p>
              <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-green-700">bulaccinocafe.com/staff</p>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg">3</div>
              <h2 className="text-xl font-bold text-gray-900">Enter Your Staff PIN</h2>
            </div>
            <div className="ml-13 pl-6 border-l-2 border-green-200">
              <p className="text-gray-600 mb-3">
                Enter the 6-digit PIN given to you by your manager. If you don't have a PIN, ask your manager.
              </p>
              <div className="bg-gray-100 rounded-lg p-4 flex justify-center gap-2">
                <div className="w-10 h-12 bg-white border-2 border-gray-300 rounded flex items-center justify-center text-xl font-bold">*</div>
                <div className="w-10 h-12 bg-white border-2 border-gray-300 rounded flex items-center justify-center text-xl font-bold">*</div>
                <div className="w-10 h-12 bg-white border-2 border-gray-300 rounded flex items-center justify-center text-xl font-bold">*</div>
                <div className="w-10 h-12 bg-white border-2 border-gray-300 rounded flex items-center justify-center text-xl font-bold">*</div>
                <div className="w-10 h-12 bg-white border-2 border-gray-300 rounded flex items-center justify-center text-xl font-bold">*</div>
                <div className="w-10 h-12 bg-white border-2 border-gray-300 rounded flex items-center justify-center text-xl font-bold">*</div>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg">4</div>
              <h2 className="text-xl font-bold text-gray-900">Register This Device</h2>
            </div>
            <div className="ml-13 pl-6 border-l-2 border-green-200">
              <p className="text-gray-600 mb-3">
                If this is your first time logging in, you'll see a "Register Device" button. Tap it to register your phone.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-700 text-sm">
                  <strong>Note:</strong> You can only have ONE registered device. If you get a new phone, ask your manager to reset your device registration.
                </p>
              </div>
            </div>
          </div>

          {/* Done */}
          <div className="bg-green-50 border-2 border-green-300 rounded-xl p-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-green-800 mb-2">You're All Set!</h2>
            <p className="text-green-700">
              Your device is now registered. You can clock in, view your schedule, and request time off.
            </p>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center text-gray-500 text-sm">
            <p>Bulaccino Cafe - Nadi, Fiji</p>
            <p>Questions? Ask your manager or supervisor.</p>
          </div>
        </div>
      </div>
    </>
  );
}
